// Strips MetaTrader broker suffixes like .p, .pi, .m, .pro from instrument symbols
export function normalizeSymbol(symbol: string): string {
  return symbol.replace(/\.(p|pi|m|pro|ecn|raw|stp|micro|mini)$/i, "");
}

// Strips the futures expiration code from a contract symbol: a CME month
// letter (F G H J K M N Q U V X Z) followed by a 1- or 2-digit year, e.g.
// MNQM6 -> MNQ, ESZ25 -> ES, NQH5 -> NQ. The 1-or-2-digit year is intentional
// — platforms like Topstep X use single-digit years (M6); do not tighten to
// \d{2}. Leaves stocks and forex pairs untouched.
export function stripFuturesExpiration(symbol: string): string {
  const upper = symbol.toUpperCase();
  const match = upper.match(/^([A-Z]{1,6})[FGHJKMNQUVXZ]\d{1,2}$/);
  return match ? match[1] : upper;
}

/**
 * Used purely to help compiler check for exhaustiveness in switch statements,
 * will never execute. See https://stackoverflow.com/a/39419171.
 */
export function assertUnreachable(_: never): never {
  throw Error("This code should be unreachable");
}

/**
 * Allows for throttling a function call while still allowing the last invocation to be executed after the throttle delay ends.
 */
export function throttleWithTrailingInvocation(
  fn: () => void,
  delayInMilliseconds: number,
): ((...args: any[]) => void) & { cancel: () => void } {
  let fnLastCallTime: number | null = null;
  let trailingInvocationTimeoutId: ReturnType<typeof setTimeout> | null = null;
  let isTrailingInvocationPending = false;

  const callFn = () => {
    fnLastCallTime = Date.now();
    fn();
  };

  const throttledFn = () => {
    const currentTime = Date.now();
    const timeSinceLastExecution = fnLastCallTime
      ? currentTime - fnLastCallTime
      : 0;

    const shouldCallImmediately =
      fnLastCallTime === null || timeSinceLastExecution >= delayInMilliseconds;

    if (shouldCallImmediately) {
      callFn();
      return;
    }

    if (!isTrailingInvocationPending) {
      isTrailingInvocationPending = true;
      const remainingDelayTime = Math.max(
        delayInMilliseconds - timeSinceLastExecution,
        0,
      );

      trailingInvocationTimeoutId = setTimeout(() => {
        callFn();
        isTrailingInvocationPending = false;
      }, remainingDelayTime);
    }
  };

  throttledFn.cancel = () => {
    if (trailingInvocationTimeoutId) {
      clearTimeout(trailingInvocationTimeoutId);
      trailingInvocationTimeoutId = null;
    }
    isTrailingInvocationPending = false;
  };

  return throttledFn as typeof throttledFn & { cancel: () => void };
}
